/**
 * D1 card media gallery — arrows, dots, swipe, hover autoplay.
 * Event-delegated so AJAX-rendered Accessories cards work too.
 */
(function () {
  'use strict';

  var SWIPE_THRESHOLD = 36;
  var MAX_IMAGES = 5;
  var HOVER_PLAY_MS = 1200;
  var hoverTimer = 0;
  var hoverGallery = null;

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function getGallery(el) {
    return el && el.closest ? el.closest('[data-d1-card-gallery]') : null;
  }

  function getState(gallery) {
    var count = parseInt(gallery.getAttribute('data-count') || '1', 10) || 1;
    var index = parseInt(gallery.getAttribute('data-index') || '0', 10) || 0;
    return { count: count, index: clamp(index, 0, count - 1) };
  }

  function setIndex(gallery, nextIndex) {
    var state = getState(gallery);
    var index = clamp(nextIndex, 0, state.count - 1);
    gallery.setAttribute('data-index', String(index));
    gallery.style.setProperty('--d1-gallery-index', String(index));

    var dots = gallery.querySelectorAll('[data-d1-gallery-dot]');
    for (var i = 0; i < dots.length; i++) {
      if (i === index) {
        dots[i].setAttribute('aria-current', 'true');
      } else {
        dots[i].removeAttribute('aria-current');
      }
    }

    var prev = gallery.querySelector('[data-d1-gallery-prev]');
    var next = gallery.querySelector('[data-d1-gallery-next]');
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= state.count - 1;
  }

  function go(gallery, delta) {
    var state = getState(gallery);
    setIndex(gallery, state.index + delta);
  }

  function nextIndex(index, count) {
    if (count < 2) return 0;
    return (index + 1) % count;
  }

  function fineHover() {
    return window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function stopHoverPlay() {
    if (hoverTimer) {
      clearInterval(hoverTimer);
      hoverTimer = 0;
    }
    hoverGallery = null;
  }

  function startHoverPlay(gallery) {
    stopHoverPlay();
    if (!gallery || reduceMotion()) return;
    var state = getState(gallery);
    if (state.count < 2) return;
    hoverGallery = gallery;
    hoverTimer = setInterval(function () {
      var current = getState(gallery);
      setIndex(gallery, nextIndex(current.index, current.count));
    }, HOVER_PLAY_MS);
  }

  function bindHoverPlay(gallery) {
    if (gallery.getAttribute('data-hover-bound') === '1') return;
    gallery.setAttribute('data-hover-bound', '1');
    if (!fineHover()) return;

    var root = (gallery.closest && gallery.closest('.card__media')) || gallery;

    root.addEventListener('pointerenter', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      markActive(gallery);
      startHoverPlay(gallery);
    });

    root.addEventListener('pointerleave', function () {
      stopHoverPlay();
      setIndex(gallery, 0);
    });
  }

  function markActive(gallery) {
    gallery.classList.add('is-active');
  }

  function bindSwipe(gallery) {
    if (gallery.getAttribute('data-swipe-bound') === '1') return;
    gallery.setAttribute('data-swipe-bound', '1');

    var startX = 0;
    var startY = 0;
    var tracking = false;
    var locked = false;

    gallery.addEventListener(
      'touchstart',
      function (e) {
        if (!e.touches || e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = true;
        locked = false;
        markActive(gallery);
      },
      { passive: true }
    );

    gallery.addEventListener(
      'touchmove',
      function (e) {
        if (!tracking || !e.touches || e.touches.length !== 1) return;
        var dx = e.touches[0].clientX - startX;
        var dy = e.touches[0].clientY - startY;
        if (!locked) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          locked = Math.abs(dx) > Math.abs(dy);
          if (!locked) tracking = false;
        }
        if (locked && e.cancelable) e.preventDefault();
      },
      { passive: false }
    );

    gallery.addEventListener(
      'touchend',
      function (e) {
        if (!tracking) return;
        tracking = false;
        if (!locked) return;
        var touch = e.changedTouches && e.changedTouches[0];
        if (!touch) return;
        var dx = touch.clientX - startX;
        if (Math.abs(dx) < SWIPE_THRESHOLD) return;
        go(gallery, dx < 0 ? 1 : -1);
      },
      { passive: true }
    );
  }

  function initGallery(gallery) {
    if (!gallery || gallery.getAttribute('data-ready') === '1') return;
    var state = getState(gallery);
    if (state.count < 2) return;
    gallery.setAttribute('data-ready', '1');
    gallery.style.setProperty('--d1-gallery-count', String(state.count));
    setIndex(gallery, state.index);
    bindSwipe(gallery);
    bindHoverPlay(gallery);
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-d1-card-gallery]');
    for (var i = 0; i < nodes.length; i++) initGallery(nodes[i]);
  }

  document.addEventListener(
    'click',
    function (e) {
      var prev = e.target.closest && e.target.closest('[data-d1-gallery-prev]');
      var next = e.target.closest && e.target.closest('[data-d1-gallery-next]');
      var dot = e.target.closest && e.target.closest('[data-d1-gallery-dot]');
      var control = prev || next || dot;
      if (!control) return;

      var gallery = getGallery(control);
      if (!gallery) return;

      e.preventDefault();
      e.stopPropagation();
      markActive(gallery);

      if (prev) go(gallery, -1);
      else if (next) go(gallery, 1);
      else if (dot) {
        var idx = parseInt(dot.getAttribute('data-d1-gallery-dot') || '0', 10) || 0;
        setIndex(gallery, idx);
      }

      if (hoverGallery === gallery) startHoverPlay(gallery);
    },
    true
  );

  document.addEventListener('keydown', function (e) {
    var gallery = getGallery(e.target);
    if (!gallery) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      go(gallery, -1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      go(gallery, 1);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Re-init after AJAX-rendered Accessories / Just Landed cards
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var nodes = mutations[i].addedNodes;
      for (var j = 0; j < nodes.length; j++) {
        var node = nodes[j];
        if (!node || node.nodeType !== 1) continue;
        if (node.matches && node.matches('[data-d1-card-gallery]')) initGallery(node);
        else initAll(node);
      }
    }
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  /**
   * Build gallery HTML for AJAX product cards.
   * @param {{images?: Array, featured_image?: object, title?: string, url?: string}} product
   * @param {{sizes?: string, widths?: string, alt?: string}} [opts]
   * @returns {string}
   */
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function imageUrl(img, width) {
    if (!img) return '';
    var src = typeof img === 'string' ? img : img.src || '';
    if (!src) return '';
    if (src.indexOf('cdn.shopify.com') === -1 && src.indexOf('shopify.com') === -1) return src;
    var sep = src.indexOf('?') === -1 ? '?' : '&';
    return src + sep + 'width=' + width;
  }

  function buildSrcset(img, widths) {
    var parts = [];
    for (var i = 0; i < widths.length; i++) {
      var w = widths[i];
      var u = imageUrl(img, w);
      if (u) parts.push(u + ' ' + w + 'w');
    }
    return parts.join(', ');
  }

  function renderMediaInnerHtml(product, opts) {
    opts = opts || {};
    var widths = opts.widths || [180, 360, 540, 720, 900];
    var sizes = opts.sizes || '(min-width: 990px) 20vw, (min-width: 750px) 30vw, 45vw';
    var title = product.title || '';
    var url = product.url || (product.handle ? '/products/' + product.handle : '#');
    var loadingAttr = opts.loading || 'lazy';
    var images = [];

    if (Array.isArray(product.images) && product.images.length) {
      images = product.images.slice(0, MAX_IMAGES);
    } else if (product.featured_image) {
      images = [product.featured_image];
    }

    if (!images.length) {
      return opts.emptyHtml || '<span class="card__media-coming-soon">Image Coming Soon</span>';
    }

    if (images.length < 2) {
      var only = images[0];
      var onlySrc = imageUrl(only, opts.singleWidth || 400);
      var onlyAlt = escapeHtml((only && only.alt) || title);
      return (
        '<img src="' +
        escapeHtml(onlySrc) +
        '" alt="' +
        onlyAlt +
        '" loading="' +
        escapeHtml(loadingAttr) +
        '" width="200" height="200">'
      );
    }

    var slides = '';
    var dots = '';
    for (var i = 0; i < images.length; i++) {
      var img = images[i];
      var src = imageUrl(img, 540);
      var srcset = buildSrcset(img, widths);
      var alt = escapeHtml((img && img.alt) || title);
      var loading = i === 0 ? loadingAttr : 'lazy';
      slides +=
        '<a class="d1-card-gallery__slide" href="' +
        escapeHtml(url) +
        '" tabindex="-1">' +
        '<img src="' +
        escapeHtml(src) +
        '" srcset="' +
        escapeHtml(srcset) +
        '" sizes="' +
        escapeHtml(sizes) +
        '" alt="' +
        alt +
        '" loading="' +
        escapeHtml(loading) +
        '" width="1000" height="1000">' +
        '</a>';
      dots +=
        '<button type="button" class="d1-card-gallery__dot" data-d1-gallery-dot="' +
        i +
        '"' +
        (i === 0 ? ' aria-current="true"' : '') +
        ' aria-label="Show image ' +
        (i + 1) +
        ' of ' +
        images.length +
        '"></button>';
    }

    return (
      '<div class="d1-card-gallery" data-d1-card-gallery data-count="' +
      images.length +
      '" data-index="0" style="--d1-gallery-index:0;--d1-gallery-count:' +
      images.length +
      '" role="group" aria-roledescription="carousel" aria-label="' +
      escapeHtml(title) +
      ' images">' +
      '<div class="d1-card-gallery__viewport">' +
      '<div class="d1-card-gallery__track">' +
      slides +
      '</div></div>' +
      '<button type="button" class="d1-card-gallery__nav d1-card-gallery__nav--prev" data-d1-gallery-prev aria-label="Previous image" disabled>' +
      '<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" focusable="false"><path d="M12.5 4.5 7 10l5.5 5.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      '<button type="button" class="d1-card-gallery__nav d1-card-gallery__nav--next" data-d1-gallery-next aria-label="Next image">' +
      '<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" focusable="false"><path d="M7.5 4.5 13 10l-5.5 5.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      '<div class="d1-card-gallery__dots" role="tablist" aria-label="Image pagination">' +
      dots +
      '</div></div>'
    );
  }

  window.D1CardGallery = {
    init: initAll,
    renderMediaInnerHtml: renderMediaInnerHtml,
    maxImages: MAX_IMAGES,
    nextIndex: nextIndex,
    hoverPlayMs: HOVER_PLAY_MS,
  };
})();
